import { Button, IconButton } from "@hortis/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@hortis/ui/dropdown-menu";
import { DotsHorizontal, Map2, Trash3 } from "@hortis/ui/icons";
import { Copy1 } from "@hortis/ui/icons/copy-1";
import { Tooltip, TooltipContent, TooltipTrigger } from "@hortis/ui/tooltip";
import type { MaterialQualifierFormat } from "generated/graphql";
import { PlantMaterialStatus } from "generated/graphql";
import { useRef, useState } from "react";
import {
  StatusPicker,
  StatusPickerLabel,
} from "src/components/pickers/status-picker";
import { stopEventPropagation } from "src/features/collection/components/stop-event-propagation";
import { LocationPicker } from "src/features/locations/components/location-picker";
import { createMaterialQualifier } from "src/utils/material-qualifiers/material-qualifiers";
import { assertAsPositiveInteger } from "src/utils/positive-integer";
import { twMerge } from "tailwind-merge";
import type { MaterialSchema } from "../add-plant-material-form/schema";
import { MakeAbsentDialog } from "./make-absent-dialog";

const Map2Icon = () => <Map2 className="h-4 w-4" />;

export const MaterialRow = ({
  index,
  duplicateMaterial,
  material,
  onClick,
  onDuplicate,
  onRemove,
  updateMaterial,
  qualifierFormat,
}: {
  index: number;
  duplicateMaterial: boolean;
  material: MaterialSchema & { id: string };
  onClick: () => void;
  onDuplicate: () => void;
  onRemove: () => void;
  updateMaterial: (material: Partial<MaterialSchema>) => void;
  qualifierFormat: MaterialQualifierFormat;
}) => {
  const statusPickerRef = useRef<HTMLButtonElement>(null);
  const [makeAbsentDialogOpen, setMakeAbsentDialogOpen] = useState(false);
  return (
    <>
      {}
      <div
        onClick={onClick}
        role="button"
        onKeyDown={(e) => {
          if (e.target === e.currentTarget && e.key === "Enter") {
            onClick();
          }
        }}
        tabIndex={0}
        className={twMerge(
          "flex items-center justify-between rounded-lg border-none px-3 py-1.5 outline-hidden ring-grey-200/70 transition duration-700 ease-out hover:bg-grey-100 focus:ring-3",
          duplicateMaterial &&
            "duration-50 bg-primary-50 ring-2 ring-primary-200/70",
        )}
        data-cy="material-row"
      >
        <div className="text-sm font-medium">
          Material{" "}
          {createMaterialQualifier(
            assertAsPositiveInteger(index + 1),
            qualifierFormat,
          )}
        </div>
        <div className="flex items-center gap-1 truncate">
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
          <div onClick={stopEventPropagation}>
            <Tooltip>
              <StatusPicker
                blockFocus
                value={material.status}
                onChange={(status) => {
                  if (status === PlantMaterialStatus.Absent) {
                    setMakeAbsentDialogOpen(true);
                    return;
                  }
                  statusPickerRef.current?.focus();
                  updateMaterial({
                    status: status ?? undefined,
                  });
                }}
                placeholder="Change status..."
              >
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    variant="tertiaryGray"
                    className="hidden px-1  hover:bg-grey-100 md:flex"
                    ref={statusPickerRef}
                    tabIndex={-1}
                  >
                    <StatusPickerLabel value={material.status} />
                  </Button>
                </TooltipTrigger>
              </StatusPicker>
              <TooltipContent side="bottom" sideOffset={-4}>
                Change status
              </TooltipContent>
            </Tooltip>
          </div>
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
          <div onClick={stopEventPropagation}>
            <Tooltip>
              <LocationPicker
                value={material.collectionSiteLocationOption}
                onChange={(location) => {
                  if (location != null) {
                    updateMaterial({
                      collectionSiteLocationOption: location,
                    });
                  }
                }}
                placeholder="Change location..."
              >
                <TooltipTrigger asChild>
                  <Button
                    startIcon={Map2Icon}
                    size="sm"
                    variant="tertiaryGray"
                    className="hidden gap-1 px-1  hover:bg-grey-100 md:flex"
                    tabIndex={-1}
                  >
                    {material.collectionSiteLocationOption.name}
                  </Button>
                </TooltipTrigger>
              </LocationPicker>
              <TooltipContent side="bottom" sideOffset={-4}>
                Change location
              </TooltipContent>
            </Tooltip>
          </div>
          <div className="flex items-center">
            <Tooltip>
              <TooltipTrigger asChild>
                <IconButton
                  icon={Copy1}
                  ariaLabel="Duplicate material"
                  size="xs"
                  variant="tertiaryGray"
                  className="hover:bg-grey-100"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDuplicate();
                  }}
                  testId="duplicate-material-button"
                  onFocusCapture={stopEventPropagation}
                />
              </TooltipTrigger>
              <TooltipContent side="bottom">Duplicate material</TooltipContent>
            </Tooltip>
            <DropdownMenu>
              <Tooltip>
                <TooltipTrigger asChild>
                  <DropdownMenuTrigger asChild>
                    <IconButton
                      icon={DotsHorizontal}
                      ariaLabel="More actions"
                      size="xs"
                      variant="tertiaryGray"
                      onClick={stopEventPropagation}
                      className="hover:bg-grey-100"
                      testId="more-material-actions-button"
                      onFocusCapture={stopEventPropagation}
                    />
                  </DropdownMenuTrigger>
                </TooltipTrigger>
                <TooltipContent side="bottom">More actions</TooltipContent>
              </Tooltip>
              <DropdownMenuContent side="left" onClick={stopEventPropagation}>
                <DropdownMenuItem
                  onSelect={onRemove}
                  data-cy="delete-material-menu-item"
                >
                  <Trash3 className="h-4 w-4" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
      <MakeAbsentDialog
        open={makeAbsentDialogOpen}
        onOpenChange={setMakeAbsentDialogOpen}
        onSubmitOverride={updateMaterial}
        selectedMaterials={[material]}
        onSuccess={() => {
          statusPickerRef.current?.focus();
        }}
        triggerRef={statusPickerRef}
      />
    </>
  );
};
